import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Brain, Compass, Sparkles, X, Check, Heart, Users, Wallet, Globe, Flame } from "lucide-react";
import { useState } from "react";
import IcebergDiagram from "@/components/IcebergDiagram";
import ProblemSection from "@/components/ProblemSection";
import HeadhuntingSection from "@/components/HeadhuntingSection";
import PromiseSection from "@/components/PromiseSection";
import Logo from "@/components/Logo";
import WhatsAppButton from "@/components/WhatsAppButton";
import SkipToContent from "@/components/SkipToContent";

const EvaProtocolPage = () => {
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

  const observeSection = (sectionId: string) => {
    return (node: HTMLDivElement | null) => {
      if (!node) return;
      
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, sectionId]));
          }
        },
        { threshold: 0.1 }
      );
      
      observer.observe(node);
    };
  };

  const breakdownCards = [
    {
      icon: Brain,
      percentage: "50%",
      letter: "E",
      title: "Emotional Core",
      subtitle: "ליבה רגשית",
      description: "דפוסי התקשרות, שפות אהבה וניהול כעסים. אם זה לא עובד, שום משיכה לא תחזיק את זה.",
      details: [
        { icon: Heart, text: "סגנון התקשרות: בטוח, נמנע או חרדתי" },
        { icon: Users, text: "ניהול קונפליקטים: איך מגיבים בכעס?" },
        { icon: Brain, text: "אינטליגנציה רגשית: היכולת להכיל מורכבות" },
      ],
      color: "from-gold/20 to-gold/5",
      borderColor: "border-gold/30",
      iconColor: "text-gold",
      bgColor: "bg-gold",
    },
    {
      icon: Compass,
      percentage: "30%",
      letter: "V",
      title: "Values",
      subtitle: "ערכים ומטרות",
      description: "הסכמה על כסף, דת, משפחה ואורח חיים. הבסיס לבית יציב לטווח ארוך.",
      details: [
        { icon: Users, text: "קריירה מול משפחה: מה קודם?" },
        { icon: Globe, text: "ארץ מול חו״ל: היכן רואים את העתיד?" },
        { icon: Wallet, text: "ניהול כספי, תפיסת עולם, חינוך ילדים" },
      ],
      color: "from-sky-400/20 to-sky-400/5",
      borderColor: "border-sky-400/30",
      iconColor: "text-sky-400",
      bgColor: "bg-sky-400",
    },
    {
      icon: Sparkles,
      percentage: "20%",
      letter: "A",
      title: "Attraction",
      subtitle: "משיכה והעדפות",
      description: "המשיכה הפיזית והכימיה. חשובה מאוד, אבל מגיעה רק אחרי שיש בסיס.",
      details: [
        { icon: Sparkles, text: "משיכה היא Gatekeeper — תנאי סף, לא היסודות" },
        { icon: Flame, text: "על קרקע יציבה: מ'התלקחות מהירה' ל'אש יציבה'" },
      ],
      color: "from-amber-400/20 to-amber-400/5",
      borderColor: "border-amber-400/30",
      iconColor: "text-amber-400",
      bgColor: "bg-amber-400",
    },
  ];

  const comparisonData = [
    { apps: "מודל כלכלי: זמן מסך", eva: "מודל כלכלי: זמן חופה" },
    { apps: "מבוסס על תמונה בלבד", eva: "מבוסס על אופי מלא" },
    { apps: "סינון ויזואלי שטחי", eva: "סינון הוליסטי (רגש-ערכים-מראה)" },
    { apps: "כמות — עשרות דייטים כושלים", eva: "איכות — מעט דייטים מדויקים" },
    { apps: "דופמין ומשחוק (Gaming)", eva: "בניית תא משפחתי (Building)" },
    { apps: "הימור סטטיסטי", eva: "ניהול סיכונים מקצועי" },
  ];

  return (
    <>
      <Helmet>
        <title>פרוטוקול EVA | השיטה המדעית להתאמה זוגית | AMON</title>
        <meta 
          name="description" 
          content="פרוטוקול EVA: הנדסה לאחור של אהבה. השיטה המדעית שמחליפה את ההימור ומבטיחה התאמה עמוקה לפני המשיכה." 
        />
      </Helmet>

      <SkipToContent />

      <div className="min-h-screen bg-navy text-cream" dir="rtl">
        {/* Header */}
        <header role="banner" className="fixed top-0 left-0 right-0 z-50 bg-navy/90 backdrop-blur-md border-b border-gold/10">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <Link to="/">
              <Logo variant="light" />
            </Link>
            <Link to="/waitlist">
              <Button variant="outline" className="border-gold text-gold hover:bg-gold hover:text-navy text-sm">
                הצטרפו לרשימת ההמתנה
              </Button>
            </Link>
          </div>
        </header>

        {/* Hero Section */}
        <section aria-labelledby="hero-heading" className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
          {/* Background effects */}
          <div className="absolute inset-0 bg-gradient-to-b from-navy via-navy-dark to-navy" />
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-sky-500/10 rounded-full blur-3xl" />
          </div>
          
          {/* Geometric patterns */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-20 right-10 w-40 h-40 border border-gold rotate-45" />
            <div className="absolute bottom-40 left-20 w-32 h-32 border border-cream rotate-12" />
            <div className="absolute top-1/2 right-1/3 w-24 h-24 border border-sky-400 -rotate-12" />
          </div>

          <main id="main-content" role="main" tabIndex={-1} className="relative container mx-auto px-4 text-center">
            <div className="animate-fade-up">
              <span className="inline-block px-4 py-2 bg-gold/10 border border-gold/30 rounded-full text-gold text-sm font-medium mb-8">
                Relationship Engineering
              </span>
            </div>
            
            <h1 id="hero-heading" className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-up opacity-0" style={{ animationDelay: "200ms", animationFillMode: "forwards" }}>
              פרוטוקול <span className="text-gold">EVA</span>:
              <br />
              הנדסה לאחור של אהבה
            </h1>
            
            <p className="text-xl md:text-2xl text-cream/80 max-w-3xl mx-auto mb-12 animate-fade-up opacity-0" style={{ animationDelay: "400ms", animationFillMode: "forwards" }}>
              למה 80% מהזוגיות נקבע <span className="text-gold font-semibold">לפני</span> שראיתם תמונה?
              <br />
              השיטה המדעית שמחליפה את ההימור.
            </p>

            <div className="animate-fade-up opacity-0" style={{ animationDelay: "600ms", animationFillMode: "forwards" }}>
              <div className="inline-flex items-center gap-2 text-cream/50 text-sm">
                <span className="w-8 h-px bg-cream/30" aria-hidden="true" />
                גללו למטה לגילוי השיטה
                <span className="w-8 h-px bg-cream/30" aria-hidden="true" />
              </div>
            </div>
          </main>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-cream/30 rounded-full flex items-start justify-center p-2">
              <div className="w-1.5 h-3 bg-gold rounded-full animate-pulse" />
            </div>
          </div>
        </section>

        {/* Problem Section - NEW */}
        <div ref={observeSection("problem")}>
          <ProblemSection isVisible={visibleSections.has("problem")} />
        </div>

        {/* Iceberg Section */}
        <section aria-labelledby="iceberg-heading" className="py-24 bg-gradient-to-b from-navy-dark to-navy relative">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 id="iceberg-heading" className="text-3xl md:text-4xl font-bold mb-4">
                מה באמת קובע <span className="text-gold">הצלחה בזוגיות</span>?
              </h2>
              <p className="text-cream/70 text-lg max-w-2xl mx-auto">
                כמו קרחון, רוב מה שקובע נמצא מתחת לפני השטח
              </p>
            </div>
            
            <IcebergDiagram />
          </div>
        </section>

        {/* Breakdown Cards Section - ENHANCED */}
        <section 
          aria-labelledby="breakdown-heading"
          ref={observeSection("breakdown")}
          className="py-24 bg-gradient-to-b from-navy to-navy-dark"
        >
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 id="breakdown-heading" className="text-3xl md:text-4xl font-bold mb-4">
                שלושת <span className="text-gold">מרכיבי ההתאמה</span>
              </h2>
              <p className="text-cream/70 text-lg">
                הפירוט המדעי של מה שבונה זוגיות יציבה
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {breakdownCards.map((card, index) => (
                <article
                  key={card.title}
                  className={`relative p-8 rounded-2xl bg-gradient-to-b ${card.color} border ${card.borderColor} backdrop-blur-sm transition-all duration-700 hover:scale-[1.02] hover:shadow-xl ${
                    visibleSections.has("breakdown") 
                      ? "opacity-100 translate-y-0" 
                      : "opacity-0 translate-y-8"
                  }`}
                  style={{ transitionDelay: `${index * 200}ms` }}
                >
                  {/* Letter badge */}
                  <div className={`absolute top-6 left-6 w-12 h-12 rounded-full ${card.bgColor} flex items-center justify-center`}>
                    <span className="text-navy font-bold text-xl">{card.letter}</span>
                  </div>

                  <div className={`w-16 h-16 rounded-2xl bg-navy flex items-center justify-center mb-6 ${card.iconColor}`}>
                    <card.icon className="w-8 h-8" aria-hidden="true" />
                  </div>
                  
                  <div className="mb-4">
                    <span className={`text-4xl font-bold ${card.iconColor}`}>
                      {card.percentage}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-cream mb-1">{card.title}</h3>
                  <p className={`text-sm ${card.iconColor} mb-4`}>{card.subtitle}</p>
                  <p className="text-cream/80 leading-relaxed mb-6">{card.description}</p>
                  
                  {/* Details list */}
                  <ul className="space-y-3">
                    {card.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start gap-3">
                        <detail.icon className={`w-4 h-4 ${card.iconColor} mt-0.5 flex-shrink-0`} aria-hidden="true" />
                        <span className="text-cream/70 text-sm">{detail.text}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Headhunting Section - NEW */}
        <div ref={observeSection("headhunting")}>
          <HeadhuntingSection isVisible={visibleSections.has("headhunting")} />
        </div>

        {/* Comparison Section - ENHANCED */}
        <section 
          aria-labelledby="comparison-heading"
          ref={observeSection("comparison")}
          className="py-24 bg-navy-dark relative overflow-hidden"
        >
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(45deg,transparent_48%,currentColor_49%,currentColor_51%,transparent_52%)] bg-[length:60px_60px]" />
          </div>

          <div className="container mx-auto px-4 relative">
            <div className="text-center mb-16">
              <h2 id="comparison-heading" className="text-3xl md:text-4xl font-bold mb-4">
                מה <span className="text-gold">ההבדל</span>?
              </h2>
              <p className="text-cream/70 text-lg">
                השוואה בין הגישה המסורתית לפרוטוקול EVA
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-4 md:gap-0">
                {/* Apps Column Header */}
                <div className="bg-red-900/30 border border-red-500/30 rounded-t-2xl md:rounded-tr-none md:rounded-l-2xl p-6 text-center">
                  <h3 className="text-xl font-bold text-red-400 mb-2">מלכודת האפליקציות</h3>
                  <p className="text-cream/50 text-sm">הדרך הישנה</p>
                </div>
                
                {/* EVA Column Header */}
                <div className="bg-gold/20 border border-gold/40 rounded-t-2xl md:rounded-tl-none md:rounded-r-2xl p-6 text-center">
                  <h3 className="text-xl font-bold text-gold mb-2">פרוטוקול EVA</h3>
                  <p className="text-cream/50 text-sm">הדרך החדשה</p>
                </div>

                {/* Comparison Rows */}
                {comparisonData.map((row, index) => (
                  <>
                    <div 
                      key={`apps-${index}`}
                      className={`bg-navy/50 border-x border-red-500/20 p-5 flex items-center gap-3 ${
                        index === comparisonData.length - 1 ? "border-b rounded-b-2xl md:rounded-b-none md:rounded-bl-2xl" : "border-b border-red-500/10"
                      } transition-all duration-500 ${
                        visibleSections.has("comparison") 
                          ? "opacity-100 translate-x-0" 
                          : "opacity-0 translate-x-8"
                      }`}
                      style={{ transitionDelay: `${index * 100}ms` }}
                    >
                      <X className="w-5 h-5 text-red-400 flex-shrink-0" aria-hidden="true" />
                      <span className="text-cream/70">{row.apps}</span>
                    </div>
                    <div 
                      key={`eva-${index}`}
                      className={`bg-gold/5 border-x border-gold/20 p-5 flex items-center gap-3 ${
                        index === comparisonData.length - 1 ? "border-b rounded-b-2xl md:rounded-b-none md:rounded-br-2xl" : "border-b border-gold/10"
                      } transition-all duration-500 ${
                        visibleSections.has("comparison") 
                          ? "opacity-100 translate-x-0" 
                          : "opacity-0 -translate-x-8"
                      }`}
                      style={{ transitionDelay: `${index * 100}ms` }}
                    >
                      <Check className="w-5 h-5 text-gold flex-shrink-0" aria-hidden="true" />
                      <span className="text-cream font-medium">{row.eva}</span>
                    </div>
                  </>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Promise Section - NEW */}
        <PromiseSection />

        {/* CTA Section */}
        <section aria-labelledby="cta-heading" className="py-24 bg-gradient-to-b from-navy to-navy-dark relative">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-gold/5 via-transparent to-transparent" aria-hidden="true" />
          
          <div className="container mx-auto px-4 text-center relative">
            <h2 id="cta-heading" className="text-3xl md:text-5xl font-bold mb-6">
              מוכנים לבדוק את ה-<span className="text-gold">DNA הזוגי</span> שלכם?
            </h2>
            <p className="text-cream/70 text-lg mb-12 max-w-2xl mx-auto">
              הצעד הראשון להתאמה מדויקת מתחיל בהבנת עצמכם
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quiz-men">
                <Button 
                  size="lg" 
                  className="bg-gold hover:bg-gold/90 text-navy font-bold px-8 py-6 text-lg rounded-xl shadow-lg shadow-gold/20 hover:shadow-gold/30 transition-all"
                >
                  אני גבר - לבדיקת התאמה
                </Button>
              </Link>
              <Link to="/quiz-women">
                <Button 
                  size="lg" 
                  className="border-2 border-cream text-cream bg-transparent hover:bg-cream hover:text-navy font-bold px-8 py-6 text-lg rounded-xl transition-all"
                >
                  אני אישה - לבדיקת התאמה
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer role="contentinfo" className="py-8 border-t border-gold/10">
          <div className="container mx-auto px-4 text-center space-y-4">
            <Link to="/" className="inline-block mb-4">
              <Logo variant="light" />
            </Link>
            <p className="text-cream/50 text-sm">
              © {new Date().getFullYear()} AMON. כל הזכויות שמורות.
            </p>
            <nav aria-label="קישורי פוטר">
              <ul className="flex justify-center gap-6 text-sm">
                <li>
                  <Link to="/privacy" className="text-cream/50 hover:text-gold transition-colors">
                    מדיניות פרטיות
                  </Link>
                </li>
                <li>
                  <Link to="/accessibility" className="text-cream/50 hover:text-gold transition-colors">
                    הצהרת נגישות
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </footer>

        <WhatsAppButton />
      </div>
    </>
  );
};

export default EvaProtocolPage;
